// src/lib/sentry.ts
import * as Sentry from "@sentry/react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { useEffect } from "react";
import { getAuth } from "firebase/auth";

/**
 * Inicializa Sentry con configuración optimizada para producción
 * Incluye error tracking, performance monitoring y replay
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "production";
  const release = import.meta.env.VITE_APP_VERSION || "unknown";

  // Solo inicializar en producción o si DSN está configurado
  if (!dsn && import.meta.env.PROD) {
    console.warn("⚠️ Sentry DSN no configurado. Error tracking deshabilitado.");
    return;
  }

  if (!dsn) {
    // En desarrollo, Sentry está deshabilitado
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Integrations
    integrations: [
      // React Router integration
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Replay para debugging visual
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),

      // Browser profiling
      Sentry.browserProfilingIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% en prod, 100% en dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% de sesiones normales
    replaysOnErrorSampleRate: 1.0, // 100% cuando hay error

    // Profiling
    profilesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Filtros de errores
    beforeSend(event, hint) {
      // Filtrar errores de extensiones del browser
      if (event.exception) {
        const errorMessage = hint.originalException?.toString() || "";

        // Ignorar errores de extensiones
        if (
          errorMessage.includes("chrome-extension://") ||
          errorMessage.includes("moz-extension://") ||
          errorMessage.includes("safari-extension://")
        ) {
          return null;
        }

        // Ignorar errores de red triviales
        if (errorMessage.includes("NetworkError") || errorMessage.includes("Failed to fetch")) {
          return null;
        }

        // Ignorar "permission-denied" de Firestore cuando ya no hay sesión: son
        // lecturas que quedaron en curso al cerrar sesión (la pantalla se va, la
        // consulta termina sin usuario y Firestore la rechaza). No afectan a nadie.
        const original = hint.originalException as { name?: string; code?: string } | undefined;
        if (original?.name === "FirebaseError" && original.code === "permission-denied") {
          let signedIn = true;
          try {
            signedIn = !!getAuth().currentUser;
          } catch {
            // Firebase todavía no inicializado: no sabemos, mejor reportar
          }
          if (!signedIn) return null;
        }
      }

      return event;
    },

    // Configuración adicional
    normalizeDepth: 10, // Profundidad de serialización de objetos
    maxValueLength: 1000, // Longitud máxima de valores
  });

  // Configurar contexto global
  Sentry.setContext("app", {
    version: release,
    environment,
  });
}

/**
 * Captura un error manualmente
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext("error_context", context);
  }
  Sentry.captureException(error);
}

/**
 * Captura un mensaje personalizado
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info") {
  Sentry.captureMessage(message, level);
}

/**
 * Configura información del usuario
 */
export function setUser(user: { id: string; email?: string; name?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Agrega breadcrumb personalizado
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Envuelve una función async para capturar errores
 */
export function withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(fn: T, errorMessage?: string): T {
  return (async (...args: unknown[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (errorMessage) {
        addBreadcrumb(errorMessage, "error", "error");
      }
      captureError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }) as T;
}

// Re-exportar componentes útiles de Sentry
export { ErrorBoundary, withErrorBoundary } from "@sentry/react";
export { Sentry };
